import { classNames } from '@shopify/css-utilities'
import { Link, Image, Money, useProductOptions, AddToCartButton } from '@shopify/hydrogen'
import styles from '../../assets/scss/components/collections/collections.module.scss'
import { ProductMetafields } from '../../lib/types'
import { useI18n } from '../i18n.client'
import { useTracking } from '../../lib/hooks/tracking.client'

export const ProductCard: React.FC<{
  eventName?: string
  isOnGreen?: boolean
  product: ProductMetafields
  showBuyButton?: boolean
}> = ({ eventName, isOnGreen = false, product, showBuyButton = false }) => {
  const [i18n] = useI18n()
  const { compareAtPriceV2, priceV2 } = product.variants?.nodes[0] ?? {}
  const isDiscounted = compareAtPriceV2?.amount && parseFloat(compareAtPriceV2.amount) > parseFloat(priceV2.amount)
  const isOutOfStock = product.variants.nodes.every(({ availableForSale }) => !availableForSale)

  return (
    <div className={classNames(styles.cardContainer, product.heroImage && styles.isHero)}>
      <Link
        className={classNames(
          styles.card,
          isOnGreen && styles.isOnGreen,
          product.thumbnailHoverImage && styles.hasHover,
        )}
        to={`/products/${product.handle}`}>
        <ProductImage product={product} />
        <div className={classNames(styles.title, isOutOfStock && styles.isOutOfStock)}>{product.title}</div>
        <div className={styles.priceContainer}>
          {!product.priceOnAsking || product.priceOnAsking.value === 'false' ? (
            <>
              <Money data={priceV2} withoutTrailingZeros />
              {isDiscounted && <Money className={styles.discounted} data={compareAtPriceV2} withoutTrailingZeros />}
              {isOutOfStock && <div className={styles.soldOut}>{i18n.translate('sold-out')}</div>}
            </>
          ) : (
            i18n.translate('poa')
          )}
        </div>
      </Link>
      {showBuyButton && <ProductButton eventName={eventName} />}
    </div>
  )
}

const ProductButton: React.FC<{ eventName?: string }> = ({ eventName }) => {
  const [i18n] = useI18n()
  const { selectedVariant } = useProductOptions()
  const { trackEvent } = useTracking()

  const handleClick = () => {
    if (eventName) {
      trackEvent(eventName)
    }

    window.scrollTo({ top: 0 })
  }

  return (
    <AddToCartButton className={styles.buyButton} onClick={handleClick} variantId={selectedVariant?.id as string}>
      {i18n.translate('make-it-mine')}
    </AddToCartButton>
  )
}

export const ProductImage: React.FC<{ product: ProductMetafields }> = ({ product }) => {
  if (product.heroImage?.reference.image) {
    const img = product.heroImage.reference.image;
    return (
      <div className={styles.heroScrollContainer}>
        <Image
          className="fit-width"
          data={img}
          alt={img.altText || product.title}
        />
      </div>
    );
  }

  if (product.thumbnailHoverImage?.reference.image && product.thumbnailImage?.reference.image) {
    const hoverImg = product.thumbnailHoverImage.reference.image;
    const thumbImg = product.thumbnailImage.reference.image;
    return (
      <div
        className={styles.backgroundImage}
        style={{ backgroundImage: `url(${thumbImg.url})` }}>
        <Image
          className="fit-width"
          data={hoverImg}
          alt={hoverImg.altText || product.title}
        />
      </div>
    );
  }

  if (product.thumbnailImage?.reference.image) {
    const thumbImg = product.thumbnailImage.reference.image;
    return (
      <Image
        alt={thumbImg.altText || product.title}
        className="fit-width"
        src={thumbImg.url}
        height={1000}
        width={811}
      />
    );
  }

  return (
    <Image
      alt=""
      className="fit-width"
      height="413"
      src="https://dummyimage.com/335x413"
      width="335"
    />
  );
}
